import { PlayCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { safeUrl } from "@/lib/sanitize";
import { listMediaBySlug } from "@/features/knowledge/api";
import type { KbMediaAsset } from "@/features/knowledge/types";

/**
 * Video card grid (Webinar replays / Tax Resolution series). The category slug is
 * the last path segment. `detail` optionally holds a thumbnail URL.
 */
export default function LibraryVideosPage() {
  const slug = useLocation().pathname.split("/").filter(Boolean).pop() ?? "";
  const [items, setItems] = useState<KbMediaAsset[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listMediaBySlug(slug)
      .then((all) => !cancelled && setItems(all.filter((i) => i.assetType !== "DIR")))
      .catch(() => !cancelled && toast.error("Could not load videos."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-48 w-full" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return <p className="text-muted-foreground text-sm">No videos in this category yet.</p>;
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((item) => (
        <a key={item.id} href={safeUrl(item.href)} target="_blank" rel="noopener noreferrer">
          <Card className="hover:shadow-card-hover h-full gap-0 overflow-hidden p-0 transition-shadow">
            <div className="bg-muted flex aspect-video items-center justify-center">
              {item.detail ? (
                <img
                  src={safeUrl(item.detail)}
                  alt=""
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              ) : (
                <PlayCircle className="text-muted-foreground size-10" />
              )}
            </div>
            <div className="p-4">
              <p className="font-medium">{item.description}</p>
            </div>
          </Card>
        </a>
      ))}
    </div>
  );
}
